import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Package, CreditCard, Truck, ClipboardCheck, Plane, CheckCircle2,
  ChevronRight, Upload, Loader2, ImageIcon, X, RefreshCw, BarChart3, Filter, FileDown, Send, Clock, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import RateEditor from '../components/RateEditor';
import Procure1688Panel from '../components/Procure1688Panel';
import BankDetailsEditor from '../components/BankDetailsEditor';
import PaymentReviewPanel from '../components/PaymentReviewPanel';
import PaymentDashboard from '../components/PaymentDashboard';
import BatchReviewCard from '../components/BatchReviewCard';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', icon: CreditCard, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', next: 'domestic_transit', nextLabel: 'Mark Paid → Domestic Transit' },
  domestic_transit: { label: 'Domestic Transit', icon: Truck, color: 'text-blue-600 bg-blue-50 border-blue-200', next: 'inspection_warehousing', nextLabel: 'Arrived → Inspection' },
  inspection_warehousing: { label: 'Inspection & Warehousing', icon: ClipboardCheck, color: 'text-purple-600 bg-purple-50 border-purple-200', next: 'international_transit', nextLabel: "Ship Int'l" },
  international_transit: { label: "Int'l Transit", icon: Plane, color: 'text-indigo-600 bg-indigo-50 border-indigo-200', next: 'completed', nextLabel: 'Mark Completed' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', next: null, nextLabel: null },
};

export default function AgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [advancingId, setAdvancingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [statusNote, setStatusNote] = useState('');
  const [qcNote, setQcNote] = useState('');
  const [requestingId, setRequestingId] = useState(null);
  const [batches, setBatches] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const adminToken = localStorage.getItem('rtr_admin_token');

  useEffect(() => {
    if (!user) { navigate('/cars/login'); return; }
    fetchAll();
    const poll = setInterval(fetchAll, 30000); // keep the queue live for agents
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, filterStatus]);

  const fetchAll = async () => {
    const headers = { Authorization: `Bearer ${adminToken}` };
    try {
      const [ordersRes, statsRes, batchesRes] = await Promise.all([
        fetch(`${API}/api/orders/admin/all${filterStatus ? `?status=${filterStatus}` : ''}`, { headers }),
        fetch(`${API}/api/orders/admin/stats`, { headers }),
        fetch(`${API}/api/payments/admin/batches`, { headers }),
      ]);
      if (ordersRes.status === 401 || statsRes.status === 401) { toast.error('Session expired — please log in again'); navigate('/cars/login'); return; }
      const ordersData = await ordersRes.json();
      const statsData = await statsRes.json();
      const batchesData = await batchesRes.json().catch(() => ({}));
      if (ordersData.success) setOrders(ordersData.data); else toast.error(ordersData.detail || 'Failed to load orders');
      if (statsData.success) setStats(statsData.data);
      if (batchesData.success) setBatches(batchesData.data);
      setRefreshKey((k) => k + 1);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const advanceStatus = async (orderId, newStatus) => {
    setAdvancingId(orderId);
    try {
      const res = await fetch(`${API}/api/orders/admin/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ status: newStatus, note: statusNote || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update status');
      toast.success(`Order #${orderId} updated to ${STATUS_CONFIG[newStatus]?.label}`);
      setStatusNote('');
      fetchAll();

      // Auto-open WhatsApp to notify client
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, '_blank');
      }
    } catch (err) { toast.error(err.message); }
    finally { setAdvancingId(null); }
  };

  const uploadPhotos = async (orderId, files) => {
    setUploadingId(orderId);
    try {
      const formData = new FormData();
      for (const f of files) formData.append('files', f);
      const res = await fetch(`${API}/api/orders/admin/${orderId}/photos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success(`${files.length} photo(s) uploaded for order #${orderId}`);
      fetchAll();
    } catch (err) { toast.error(err.message); }
    finally { setUploadingId(null); }
  };

  const cfg = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.pending_payment;
  const qcStyle = (s) => ({
    pending_approval: 'bg-amber-50 border-amber-200 text-amber-800',
    approved: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    issue_reported: 'bg-red-50 border-red-200 text-red-800',
  }[s] || 'bg-gray-50 border-gray-200 text-gray-700');

  const requestApproval = async (orderId) => {
    setRequestingId(orderId);
    const waWin = window.open('', '_blank');
    try {
      const res = await fetch(`${API}/api/orders/admin/${orderId}/request-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ note: qcNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to request approval');
      toast.success(`Order #${orderId} sent to customer for approval`);
      setQcNote('');
      fetchAll();
      if (waWin) waWin.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setRequestingId(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="agent-dashboard">
      {/* Nav */}
      <nav className="bg-blue-950 shadow-sm sticky top-0 z-40 border-b border-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/cars/admin" className="flex items-center gap-2 text-blue-200 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-white font-bold text-sm">Agent Order Dashboard</span>
          </div>
          <button onClick={fetchAll} className="text-blue-300 hover:text-white text-sm flex items-center gap-1" data-testid="refresh-orders">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <PaymentDashboard token={adminToken} refreshKey={refreshKey} onSelectOrder={(id) => { setFilterStatus(''); setSelected(id); setTimeout(() => document.querySelector(`[data-testid="agent-order-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }} />
        <RateEditor token={adminToken} />
        <BankDetailsEditor token={adminToken} />

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="order-stats">
            <div className="bg-white rounded-xl border p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-xs text-gray-500 font-medium">Total Orders</div>
            </div>
            <div className={`rounded-xl border p-4 text-center cursor-pointer hover:shadow-md transition-shadow text-sky-700 bg-sky-50 border-sky-200 ${filterStatus === 'awaiting_verification' ? 'ring-2 ring-orange-500' : ''} ${stats.awaiting_verification ? 'animate-pulse' : ''}`}
              onClick={() => setFilterStatus(filterStatus === 'awaiting_verification' ? '' : 'awaiting_verification')} data-testid="stat-awaiting-verification">
              <div className="text-2xl font-bold">{stats.awaiting_verification || 0}</div>
              <div className="text-xs font-medium">Receipts to Verify</div>
            </div>
            {Object.entries(stats.by_status).map(([s, count]) => {
              const c = cfg(s);
              return (
                <div key={s} className={`rounded-xl border p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${filterStatus === s ? 'ring-2 ring-orange-500' : ''} ${c.color}`}
                  onClick={() => setFilterStatus(filterStatus === s ? '' : s)}>
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs font-medium">{c.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter */}
        {filterStatus && (
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Filtering: <strong>{filterStatus === 'awaiting_verification' ? 'Receipts to Verify' : cfg(filterStatus).label}</strong></span>
            <button onClick={() => setFilterStatus('')} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Combined payments awaiting verification */}
        {batches.length > 0 && (
          <div className="space-y-3 mb-6" data-testid="batch-review-list">
            {batches.map((b) => <BatchReviewCard key={b.id} batch={b} token={adminToken} onUpdated={fetchAll} onSelectOrder={(id) => setSelected(id)} />)}
          </div>
        )}

        {/* Orders list */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{filterStatus ? 'No orders with this status' : 'No orders yet'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const c = cfg(order.status);
              const Icon = c.icon;
              const isExpanded = selected === order.id;
              return (
                <div key={order.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`agent-order-${order.id}`}>
                  <button onClick={() => setSelected(isExpanded ? null : order.id)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${c.color}`}><Icon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-mono text-gray-400">#{order.id}</span>
                        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${c.color}`}>{c.label}</span>
                        {order.payment?.status === 'awaiting_verification' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-sky-100 text-sky-800 border-sky-300 animate-pulse" data-testid={`agent-payment-badge-${order.id}`}>Receipt to verify</span>}
                        <span className="text-xs text-gray-400">{order.client_name} ({order.client_whatsapp})</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{order.product_title}</h3>
                      <p className="text-xs text-gray-500">Qty: {order.quantity} | ¥{order.total_rmb?.toFixed(2)} total | {order.platform}</p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
                      {/* Order details */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="text-gray-400 block">Unit Price</span><span className="font-semibold">¥{order.price_rmb}</span></div>
                        <div><span className="text-gray-400 block">Quantity</span><span className="font-semibold">{order.quantity}</span></div>
                        <div><span className="text-gray-400 block">Domestic Ship</span><span className="font-semibold">¥{order.domestic_shipping_rmb}</span></div>
                        <div><span className="text-gray-400 block">Total</span><span className="font-semibold text-orange-600">¥{order.total_rmb?.toFixed(2)}</span></div>
                      </div>

                      {order.sku_selections?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {order.sku_selections.map((s, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100">{s.name}: {s.value}</span>
                          ))}
                        </div>
                      )}

                      {order.special_instructions && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                          <strong>Instructions:</strong> {order.special_instructions}
                        </div>
                      )}

                      {/* Payment verification */}
                      {(order.status === 'pending_payment' || order.payment) && (
                        <PaymentReviewPanel order={order} token={adminToken} onUpdated={fetchAll} />
                      )}

                      {/* 1688 procurement */}
                      {['domestic_transit', 'inspection_warehousing'].includes(order.status) || order.procurement ? (
                        <Procure1688Panel order={order} token={adminToken} onUpdated={fetchAll} />
                      ) : null}

                      {/* Inspection Photos */}
                      {order.inspection_photos?.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1 mb-2"><ImageIcon className="w-3 h-3" /> Inspection Photos ({order.inspection_photos.length})</span>
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {order.inspection_photos.map((p) => (
                              <img key={p.id} src={`${API}/api/cars/images/${p.path.replace('rtr-cars/cars/', '')}`} alt="QC" className="w-full aspect-square object-cover rounded-lg border" />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Photo upload (only for inspection stage) */}
                      {order.status === 'inspection_warehousing' && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <span className="text-xs font-semibold text-purple-700 mb-2 block">Upload Inspection Photos</span>
                          <input
                            type="file" ref={fileInputRef} multiple accept="image/*"
                            className="hidden"
                            onChange={(e) => { if (e.target.files?.length) uploadPhotos(order.id, Array.from(e.target.files)); }}
                            data-testid={`photo-upload-${order.id}`}
                          />
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingId === order.id}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                            data-testid={`upload-btn-${order.id}`}
                          >
                            {uploadingId === order.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                            Select Photos
                          </Button>
                          {order.inspection_photos?.length > 0 && !order.qc_review && (
                            <div className="mt-3 pt-3 border-t border-purple-200 flex flex-col sm:flex-row gap-2">
                              <input
                                value={qcNote} onChange={(e) => setQcNote(e.target.value)}
                                placeholder="Inspector note for the customer (optional)..."
                                className="flex-1 px-3 py-2 border border-purple-200 rounded-lg text-sm focus:border-purple-500 focus:outline-none"
                                data-testid={`qc-note-${order.id}`}
                              />
                              <Button onClick={() => requestApproval(order.id)} disabled={requestingId === order.id}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs whitespace-nowrap" data-testid={`request-approval-btn-${order.id}`}>
                                {requestingId === order.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                Send to Customer for Approval
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* QC review state */}
                      {order.qc_review && (
                        <div className={`rounded-lg border p-3 text-sm ${qcStyle(order.qc_review.status)}`} data-testid={`qc-status-${order.id}`}>
                          <div className="font-semibold flex items-center gap-2">
                            {order.qc_review.status === 'pending_approval' && <><Clock className="w-4 h-4" /> Awaiting customer approval ({order.qc_review.photo_count} photos sent {new Date(order.qc_review.requested_at).toLocaleDateString()})</>}
                            {order.qc_review.status === 'approved' && <><CheckCircle2 className="w-4 h-4" /> Customer APPROVED the goods on {new Date(order.qc_review.decided_at).toLocaleString()}</>}
                            {order.qc_review.status === 'issue_reported' && <><AlertTriangle className="w-4 h-4" /> Customer reported an ISSUE on {new Date(order.qc_review.decided_at).toLocaleString()}</>}
                          </div>
                          {order.qc_review.note && <p className="text-xs mt-1 opacity-80">Inspector note: {order.qc_review.note}</p>}
                          {order.qc_review.client_comment && <p className="text-xs mt-1 font-medium">Customer comment: "{order.qc_review.client_comment}"</p>}
                          {order.qc_review.status === 'issue_reported' && order.status === 'inspection_warehousing' && (
                            <button onClick={() => requestApproval(order.id)} className="mt-2 text-xs underline font-medium" data-testid={`resend-approval-btn-${order.id}`}>Re-send updated photos for approval</button>
                          )}
                        </div>
                      )}

                      {/* Download Inspection Report */}
                      <a
                        href={`${API}/api/orders/admin/${order.id}/inspection-report`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          fetch(`${API}/api/orders/admin/${order.id}/inspection-report`, { headers: { Authorization: `Bearer ${adminToken}` } })
                            .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                            .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `RTR-Inspection-${order.id}.pdf`; a.click(); URL.revokeObjectURL(url); })
                            .catch(() => toast.error('Failed to generate report'));
                        }}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 w-fit transition-colors"
                        data-testid={`download-report-${order.id}`}
                      >
                        <FileDown className="w-4 h-4" /> Download Inspection Report PDF
                      </a>

                      {/* Status advance */}
                      {c.next && order.status !== 'pending_payment' && (
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          <input
                            value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
                            placeholder="Add a note (optional)..."
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                            data-testid={`status-note-${order.id}`}
                          />
                          <Button
                            onClick={() => advanceStatus(order.id, c.next)}
                            disabled={advancingId === order.id}
                            className="bg-orange-600 hover:bg-orange-700 text-white text-xs whitespace-nowrap"
                            data-testid={`advance-btn-${order.id}`}
                          >
                            {advancingId === order.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                            {c.nextLabel}
                          </Button>
                        </div>
                      )}

                      {/* Status history */}
                      <div className="text-xs text-gray-500 space-y-1">
                        {order.status_history?.map((h, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                            <span>{cfg(h.status).label}</span>
                            {h.note && <span className="text-gray-400">— {h.note}</span>}
                            <span className="ml-auto text-gray-400">{new Date(h.timestamp).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
